import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Loader2, ArrowLeft, ChevronRight, CheckCircle2, AlertTriangle, Factory, Lock, Truck,
  Cog, ClipboardList, Package, Beaker, FileCheck2, Wrench,
} from "lucide-react";
import AuditTrail from "@/components/AuditTrail";

const STATUS_CHIP = {
  Pending: "bg-slate-100 text-slate-700 border-slate-200",
  "In Progress": "bg-amber-50 text-amber-800 border-amber-200",
  Ongoing: "bg-amber-50 text-amber-800 border-amber-200",
  "Under Machining": "bg-amber-50 text-amber-800 border-amber-200",
  Issued: "bg-blue-50 text-blue-800 border-blue-200",
  Completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Released: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Ready: "bg-teal-50 text-teal-800 border-teal-200",
  Dispatched: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Rejected: "bg-red-50 text-red-700 border-red-200",
  Waived: "bg-violet-50 text-violet-700 border-violet-200",
};

function StatusChip({ status, testid }) {
  const cls = STATUS_CHIP[status] || "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span data-testid={testid}
          className={`inline-flex items-center gap-1 border px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase font-mono ${cls}`}>
      {status}
    </span>
  );
}

function SectionCard({ title, icon: Icon, children, badge, testid, locked, delayed, description }) {
  return (
    <Card data-testid={testid} className={`border-slate-200 ${locked ? "bg-slate-50/50" : ""} ${delayed ? "ring-1 ring-red-200" : ""}`}>
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          {Icon && <Icon className={`w-4 h-4 ${delayed ? "text-red-600" : "text-slate-500"}`} />}
          <div>
            <div className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>{title}</div>
            {description && <div className="text-[11px] text-slate-500">{description}</div>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {delayed && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded">
              <AlertTriangle className="w-3 h-3" /> Delayed
            </span>
          )}
          {locked && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-500 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded">
              <Lock className="w-3 h-3" /> Locked
            </span>
          )}
          {badge}
        </div>
      </div>
      <div className="p-5 space-y-3 text-sm">{children}</div>
    </Card>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function KV({ label, value, mono }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</dt>
      <dd className={`text-right text-slate-800 ${mono ? "font-mono text-xs" : "text-sm"}`}>
        {value != null && value !== "" ? value : <span className="text-slate-300">—</span>}
      </dd>
    </div>
  );
}

export default function LifecycleDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [waiveDialog, setWaiveDialog] = useState(false);
  const [waiveStage, setWaiveStage] = useState("design");
  const [waiveReason, setWaiveReason] = useState("");

  async function refresh() {
    setLoading(true);
    const { data } = await api.get(`/lifecycle/${id}`);
    setD(data);
    setLoading(false);
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

  if (loading || !d) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-900" /></div>;

  const lc = d.lifecycle;
  const perm = d.permissions;
  const isRecon = lc.manufacturing_type === "Reconditioning";
  const waived = new Set((lc.waivers || []).map((w) => w.stage));
  const delaysByStage = Object.fromEntries((d.delays || []).map((x) => [x.stage, x]));

  async function patch(sub, body, successMsg) {
    setSaving(true);
    try {
      const { data } = await api.patch(`/lifecycle/${id}/${sub}`, body);
      setD(data);
      toast.success(successMsg || "Updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  async function waive() {
    if (!waiveReason.trim()) { toast.error("Reason required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/lifecycle/${id}/waive`, { stage: waiveStage, reason: waiveReason });
      setD(data);
      toast.success(`${waiveStage} waived`);
      setWaiveDialog(false); setWaiveReason("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  return (
    <div data-testid="lifecycle-detail-page" className="space-y-5 pb-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Link to="/lifecycle" className="hover:text-slate-900 flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Lifecycle Tracker
        </Link>
        <ChevronRight className="w-3 h-3" />
        <Link to={`/cylinders/${d.cylinder.id}`} className="hover:text-slate-900 font-mono">
          #{d.cylinder.cylinder_number}
        </Link>
      </div>

      {/* Header */}
      <Card className="border-slate-200 p-5">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-red-600 font-semibold flex items-center gap-1.5">
              <Factory className="w-3 h-3" /> Cylinder Lifecycle
            </div>
            <div className="mt-1 font-mono text-xl md:text-2xl font-bold text-slate-900">Cylinder #{d.cylinder.cylinder_number}</div>
            <div className="mt-2 text-sm text-slate-600">
              {d.customer_name} · {d.plant_name}
              <span className={`ml-2 text-[10px] uppercase font-semibold font-mono px-1.5 py-0.5 rounded border ${
                isRecon ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-blue-50 text-blue-800 border-blue-200"}`}>
                {isRecon ? "Reconditioning" : "New Manufacturing"}
              </span>
            </div>
          </div>
          <div className="flex flex-col md:items-end gap-2 text-sm">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Current Phase</div>
            <div data-testid="lc-current-phase" className="font-mono text-lg font-bold text-blue-900">{d.current_phase}</div>
            {d.delays.length > 0 && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded">
                <AlertTriangle className="w-3 h-3" /> {d.delays.length} stage(s) delayed
              </span>
            )}
          </div>
        </div>
      </Card>

      {/* Waivers banner (recon only, hod/admin only) */}
      {isRecon && perm.can_waive && (
        <Card className="border-violet-200 bg-violet-50/40 p-4 flex items-center justify-between gap-3">
          <div className="text-sm">
            <div className="font-semibold text-violet-900">Reconditioning Waivers</div>
            <div className="text-xs text-violet-700">If a reconditioning cylinder does not need design work, Design HOD can waive Design / Customer Drawing / BOM stages individually.</div>
            {lc.waivers?.length > 0 && (
              <div className="mt-2 flex gap-2 flex-wrap">
                {lc.waivers.map((w, idx) => (
                  <span key={idx} className="text-[10px] uppercase font-semibold font-mono bg-violet-100 text-violet-800 border border-violet-200 px-1.5 py-0.5 rounded">
                    {w.stage} waived · {w.waived_by_name}
                  </span>
                ))}
              </div>
            )}
          </div>
          <Button size="sm" variant="outline" data-testid="waive-open" onClick={() => setWaiveDialog(true)}
                  className="border-violet-300 text-violet-800 hover:bg-violet-100">
            Waive Stage
          </Button>
        </Card>
      )}

      {/* Engineering Summary */}
      <SectionCard title="Engineering" icon={Wrench} testid="lc-engineering-summary"
                   description="Design, drawing & HOD approval — Module 4"
                   badge={<StatusChip status={d.engineering.status || "Pending"} testid="lc-eng-chip" />}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2">
          <KV label="Drawing #" value={d.engineering.drawing_number} mono />
          <KV label="Revision" value={d.engineering.current_revision} mono />
          <KV label="Released" value={d.engineering.released_date} mono />
          <KV label="Approved By" value={d.engineering.approved_by_name} />
        </div>
        <div className="pt-2 text-xs">
          <Link to={`/engineering/${d.cylinder.id}`} className="text-blue-900 font-semibold hover:underline">
            Open Engineering module →
          </Link>
        </div>
      </SectionCard>

      {/* Reconditioning-only cards */}
      {isRecon && (
        <>
          <SectionCard title="Dismantling" icon={Cog} testid="lc-dismantling"
                       description="Break down the received unit"
                       badge={<StatusChip status={lc.reconditioning.dismantling.status} />}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Status">
                <Select disabled={!perm.can_edit_machining || waived.has("design")}
                        value={lc.reconditioning.dismantling.status}
                        onValueChange={(v) => patch("reconditioning", { section: "dismantling", status: v, notes: lc.reconditioning.dismantling.notes })}>
                  <SelectTrigger data-testid="dismantling-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Pending", "In Progress", "Completed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <KV label="Started" value={lc.reconditioning.dismantling.started_at?.slice(0, 10)} mono />
              <KV label="Completed" value={lc.reconditioning.dismantling.completed_at?.slice(0, 10)} mono />
            </div>
          </SectionCard>
          <SectionCard title="Analysis" icon={Beaker} testid="lc-analysis"
                       description="Assess damage and decide if design work is required"
                       badge={<StatusChip status={lc.reconditioning.analysis.status} />}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Status">
                <Select disabled={!perm.can_edit_machining}
                        value={lc.reconditioning.analysis.status}
                        onValueChange={(v) => patch("reconditioning", { section: "analysis", status: v, notes: lc.reconditioning.analysis.notes, design_required: lc.reconditioning.analysis.design_required })}>
                  <SelectTrigger data-testid="analysis-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Pending", "In Progress", "Completed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Design Required?">
                <Select disabled={!perm.can_edit_machining}
                        value={lc.reconditioning.analysis.design_required == null ? "unknown" : (lc.reconditioning.analysis.design_required ? "yes" : "no")}
                        onValueChange={(v) => patch("reconditioning", { section: "analysis", status: lc.reconditioning.analysis.status, notes: lc.reconditioning.analysis.notes, design_required: v === "yes" ? true : (v === "no" ? false : null) })}>
                  <SelectTrigger data-testid="design-required"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unknown">Unknown</SelectItem>
                    <SelectItem value="yes">Yes — need design + drawing</SelectItem>
                    <SelectItem value="no">No — skip design (HOD must waive)</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <KV label="Completed" value={lc.reconditioning.analysis.completed_at?.slice(0, 10)} mono />
            </div>
            {lc.reconditioning.analysis.design_required === false && !waived.has("design") && (
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                Analysis says no design needed — Design HOD should waive Design / Customer Drawing / BOM to unblock downstream stages.
              </div>
            )}
          </SectionCard>
        </>
      )}

      {/* BOM */}
      <SectionCard title="BOM Preparation" icon={ClipboardList} testid="lc-bom"
                   description="MES only tracks BOM readiness — the BOM itself lives outside"
                   delayed={!!delaysByStage.bom}
                   locked={waived.has("bom")}
                   badge={<StatusChip status={waived.has("bom") ? "Waived" : lc.bom.status} testid="lc-bom-chip" />}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="BOM Status">
            <Select disabled={!perm.can_edit_bom || waived.has("bom")}
                    value={lc.bom.status}
                    onValueChange={(v) => patch("bom", { status: v, remarks: lc.bom.remarks }, "BOM status updated")}>
              <SelectTrigger data-testid="bom-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Pending", "In Progress", "Completed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <KV label="Prepared By" value={lc.bom.prepared_by_name} />
          <KV label="Completion Date" value={lc.bom.completion_date} mono />
        </div>
      </SectionCard>

      {/* Design Review Meeting */}
      <SectionCard title="Design Review Meeting" icon={ClipboardList} testid="lc-design-review"
                   description="Cannot mark YES until BOM is Completed (or waived)"
                   badge={<StatusChip status={lc.design_review?.review_meeting_done ? "Completed" : "Pending"} testid="lc-review-chip" />}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Review Meeting Done">
            <Select disabled={!perm.can_edit_design_review}
                    value={lc.design_review?.review_meeting_done ? "yes" : "no"}
                    onValueChange={(v) => patch("design-review", { review_meeting_done: v === "yes", remarks: lc.design_review?.remarks }, "Review meeting updated")}>
              <SelectTrigger data-testid="review-done"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="no">No</SelectItem>
                <SelectItem value="yes">Yes</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <KV label="Review Date" value={lc.design_review?.review_date} mono />
          <KV label="Reviewed By" value={lc.design_review?.reviewed_by_name} />
          <KV label="Last Updated" value={lc.design_review?.updated_at?.slice(0, 10)} mono />
          <div className="md:col-span-4">
            <Field label="Remarks">
              <Textarea disabled={!perm.can_edit_design_review} rows={2}
                        data-testid="review-remarks"
                        defaultValue={lc.design_review?.remarks || ""}
                        onBlur={(e) => e.target.value !== (lc.design_review?.remarks || "") &&
                          patch("design-review", { review_meeting_done: lc.design_review?.review_meeting_done, remarks: e.target.value })} />
            </Field>
          </div>
        </div>
      </SectionCard>

      {/* Purchase Tracking (MR Number + Material Status) */}
      <SectionCard title="Purchase Tracking" icon={Package} testid="lc-purchase-tracking"
                   description="Purchase is handled in an external ERP · MES only tracks status"
                   badge={<StatusChip status={lc.purchase_tracking?.material_status || "Pending"} testid="lc-purchase-chip" />}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="MR Number">
            <Input disabled={!perm.can_edit_purchase} data-testid="purchase-mr-number"
                   className="font-mono"
                   defaultValue={lc.purchase_tracking?.mr_number || ""}
                   placeholder="Manual"
                   onBlur={(e) => e.target.value !== (lc.purchase_tracking?.mr_number || "") &&
                     patch("purchase", { mr_number: e.target.value, material_status: lc.purchase_tracking?.material_status, remarks: lc.purchase_tracking?.remarks })} />
          </Field>
          <Field label="Material Status">
            <Select disabled={!perm.can_edit_purchase}
                    value={lc.purchase_tracking?.material_status || "Pending"}
                    onValueChange={(v) => patch("purchase", { material_status: v, mr_number: lc.purchase_tracking?.mr_number, remarks: lc.purchase_tracking?.remarks })}>
              <SelectTrigger data-testid="purchase-material-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Pending", "Partially Received", "Completed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <KV label="Updated By" value={lc.purchase_tracking?.updated_by_name} />
          <KV label="Last Updated" value={lc.purchase_tracking?.updated_at?.slice(0, 10)} mono />
          <div className="md:col-span-4">
            <Field label="Remarks">
              <Textarea disabled={!perm.can_edit_purchase} rows={2}
                        data-testid="purchase-remarks"
                        defaultValue={lc.purchase_tracking?.remarks || ""}
                        onBlur={(e) => e.target.value !== (lc.purchase_tracking?.remarks || "") &&
                          patch("purchase", { remarks: e.target.value })} />
            </Field>
          </div>
        </div>
      </SectionCard>

      {/* Subcontract Tracking */}
      <SectionCard title="Subcontract Tracking" icon={Cog} testid="lc-subcontract"
                   description="Updated by the Subcontract department"
                   badge={<StatusChip status={lc.subcontract_tracking?.status || "Not Required"} testid="lc-subcontract-chip" />}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Status">
            <Select disabled={!perm.can_edit_subcontract}
                    value={lc.subcontract_tracking?.status || "Not Required"}
                    onValueChange={(v) => patch("subcontract", { status: v, remarks: lc.subcontract_tracking?.remarks })}>
              <SelectTrigger data-testid="subcontract-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Not Required", "Sent", "Under Process", "Received", "Completed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <KV label="Updated By" value={lc.subcontract_tracking?.updated_by_name} />
          <KV label="Last Updated" value={lc.subcontract_tracking?.updated_at?.slice(0, 10)} mono />
          <div />
          <div className="md:col-span-4">
            <Field label="Remarks">
              <Textarea disabled={!perm.can_edit_subcontract} rows={2}
                        data-testid="subcontract-remarks"
                        defaultValue={lc.subcontract_tracking?.remarks || ""}
                        onBlur={(e) => e.target.value !== (lc.subcontract_tracking?.remarks || "") &&
                          patch("subcontract", { status: lc.subcontract_tracking?.status || "Not Required", remarks: e.target.value })} />
            </Field>
          </div>
        </div>
      </SectionCard>

      {/* Cylinder Readiness (Quality) */}
      <SectionCard title="Cylinder Readiness" icon={CheckCircle2} testid="lc-cylinder-readiness"
                   description="Verified by Quality — three physical readiness checks"
                   badge={<StatusChip status={lc.cylinder_readiness?.ready_for_assembly ? "Completed" : "Pending"} testid="lc-readiness-chip" />}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            ["all_raw_material_received", "All Raw Material Received", "readiness-raw-material"],
            ["seal_kit_received", "Seal Kit Received", "readiness-seal-kit"],
            ["ready_for_assembly", "Ready for Assembly", "readiness-ready-assembly"],
          ].map(([key, label, tid]) => {
            const v = !!(lc.cylinder_readiness || {})[key];
            return (
              <Field key={key} label={label}>
                <Select disabled={!perm.can_edit_readiness}
                        value={v ? "yes" : "no"}
                        onValueChange={(newV) => patch("cylinder-readiness", { [key]: newV === "yes", remarks: lc.cylinder_readiness?.remarks }, `${label} → ${newV.toUpperCase()}`)}>
                  <SelectTrigger data-testid={tid}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="yes">Yes</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            );
          })}
          <KV label="Verified By" value={lc.cylinder_readiness?.verified_by_name} />
          <KV label="Verification Date" value={lc.cylinder_readiness?.verification_date} mono />
          <KV label="Last Updated" value={lc.cylinder_readiness?.updated_at?.slice(0, 10)} mono />
          <div className="md:col-span-3">
            <Field label="Remarks">
              <Textarea disabled={!perm.can_edit_readiness} rows={2}
                        data-testid="readiness-remarks"
                        defaultValue={lc.cylinder_readiness?.remarks || ""}
                        onBlur={(e) => e.target.value !== (lc.cylinder_readiness?.remarks || "") &&
                          patch("cylinder-readiness", { remarks: e.target.value })} />
            </Field>
          </div>
        </div>
      </SectionCard>

      {/* Procurement */}
      <SectionCard title="Procurement Readiness" icon={Package} testid="lc-procurement"
                   description="Raw Material · BOC · Seal Kit readiness (no vendor/qty tracking)">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            ["raw_material", "Raw Material", "raw-material"],
            ["boc", "BOC (Bought-Out Components)", "boc"],
            ["seal_kit", "Seal Kit", "seal-kit"],
          ].map(([k, label, tid]) => {
            const p = lc.procurement[k];
            const late = !!delaysByStage[k];
            return (
              <div key={k} className={`border rounded p-3 ${late ? "border-red-200 bg-red-50/30" : "border-slate-200"}`}>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center justify-between">
                  <span>{label}</span>
                  {late && <AlertTriangle className="w-3 h-3 text-red-600" />}
                </div>
                <Select disabled={!perm.can_edit_procurement}
                        value={p.status}
                        onValueChange={(v) => patch("procurement", { kind: k, status: v }, `${label} → ${v}`)}>
                  <SelectTrigger data-testid={`proc-${tid}-status`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Pending", "Issued", "Completed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="mt-2 text-[10px] font-mono text-slate-500">
                  {p.updated_by_name || "—"} · {p.updated_at?.slice(0, 10) || "—"}
                </div>
              </div>
            );
          })}
        </div>
      </SectionCard>

      {/* Machining */}
      <SectionCard title="Machining" icon={Cog} testid="lc-machining"
                   description="Tube & Piston Rod status only — no machine names / hours">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            ["tube", "Tube", "tube"],
            ["piston_rod", "Piston Rod", "piston-rod"],
          ].map(([k, label, tid]) => {
            const m = lc.machining[k];
            const late = !!delaysByStage[k];
            return (
              <div key={k} className={`border rounded p-3 ${late ? "border-red-200 bg-red-50/30" : "border-slate-200"}`}>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center justify-between">
                  <span>{label}</span>
                  {late && <span className="text-[10px] text-red-700 font-bold">Delayed +{late.delay_days || 0}d</span>}
                </div>
                <Select disabled={!perm.can_edit_machining}
                        value={m.status}
                        onValueChange={(v) => patch("machining", { kind: k, status: v }, `${label} → ${v}`)}>
                  <SelectTrigger data-testid={`mach-${tid}-status`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Pending", "Under Machining", "Completed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="mt-2 text-[10px] font-mono text-slate-500">
                  Started: {m.started_at?.slice(0, 10) || "—"} · Done: {m.completed_at?.slice(0, 10) || "—"}
                </div>
              </div>
            );
          })}
        </div>
      </SectionCard>

      {/* Assembly */}
      <SectionCard title="Assembly" icon={Cog} testid="lc-assembly"
                   description="Cannot begin until Tube AND Piston Rod are Completed"
                   delayed={!!delaysByStage.assembly}
                   badge={<StatusChip status={lc.assembly.status} testid="lc-assembly-chip" />}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Status">
            <Select disabled={!perm.can_edit_assembly}
                    value={lc.assembly.status}
                    onValueChange={(v) => patch("assembly", { status: v, engineer_id: lc.assembly.engineer_id, remarks: lc.assembly.remarks }, `Assembly → ${v}`)}>
              <SelectTrigger data-testid="assembly-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Pending", "In Progress", "Completed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Assembly Engineer">
            <Select disabled={!perm.can_edit_assembly}
                    value={lc.assembly.engineer_id || "none"}
                    onValueChange={(v) => patch("assembly", { status: lc.assembly.status, engineer_id: v === "none" ? null : v, remarks: lc.assembly.remarks })}>
              <SelectTrigger data-testid="assembly-engineer"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Unassigned</SelectItem>
                {(d.engineers || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <KV label="Start Date" value={lc.assembly.start_date} mono />
          <KV label="Completion Date" value={lc.assembly.completion_date} mono />
        </div>
      </SectionCard>

      {/* Testing */}
      <SectionCard title="Testing" icon={Beaker} testid="lc-testing"
                   description="Pressure test — Pass / Fail"
                   delayed={!!delaysByStage.testing}
                   badge={<StatusChip status={lc.testing.status} testid="lc-testing-chip" />}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Status">
            <Select disabled={!perm.can_edit_testing}
                    value={lc.testing.status}
                    onValueChange={(v) => patch("testing", { status: v, engineer_id: lc.testing.engineer_id, test_date: lc.testing.test_date, pressure_test: lc.testing.pressure_test, remarks: lc.testing.remarks }, `Testing → ${v}`)}>
              <SelectTrigger data-testid="testing-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Pending", "In Progress", "Completed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Test Engineer">
            <Select disabled={!perm.can_edit_testing}
                    value={lc.testing.engineer_id || "none"}
                    onValueChange={(v) => patch("testing", { status: lc.testing.status, engineer_id: v === "none" ? null : v, test_date: lc.testing.test_date, pressure_test: lc.testing.pressure_test, remarks: lc.testing.remarks })}>
              <SelectTrigger data-testid="testing-engineer"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Unassigned</SelectItem>
                {(d.engineers || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Test Date">
            <Input type="date" disabled={!perm.can_edit_testing}
                   value={lc.testing.test_date || ""}
                   data-testid="testing-date"
                   onChange={(e) => patch("testing", { status: lc.testing.status, engineer_id: lc.testing.engineer_id, test_date: e.target.value, pressure_test: lc.testing.pressure_test, remarks: lc.testing.remarks })}
                   className="font-mono" />
          </Field>
          <Field label="Pressure Test">
            <Select disabled={!perm.can_edit_testing}
                    value={lc.testing.pressure_test || "none"}
                    onValueChange={(v) => patch("testing", { status: lc.testing.status, engineer_id: lc.testing.engineer_id, test_date: lc.testing.test_date, pressure_test: v === "none" ? null : v, remarks: lc.testing.remarks })}>
              <SelectTrigger data-testid="testing-pressure"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not tested</SelectItem>
                <SelectItem value="Pass">Pass</SelectItem>
                <SelectItem value="Fail">Fail</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
      </SectionCard>

      {/* FIR */}
      <SectionCard title="Final Inspection Report (FIR)" icon={FileCheck2} testid="lc-fir"
                   description="Cannot mark Done until Testing is Completed"
                   badge={<StatusChip status={lc.fir.done ? "Completed" : "Pending"} testid="lc-fir-chip" />}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="FIR Done">
            <Select disabled={!perm.can_edit_fir}
                    value={lc.fir.done ? "yes" : "no"}
                    onValueChange={(v) => patch("fir", { done: v === "yes", date: lc.fir.date, remarks: lc.fir.remarks }, `FIR ${v === "yes" ? "marked done" : "reverted"}`)}>
              <SelectTrigger data-testid="fir-done"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="no">No</SelectItem>
                <SelectItem value="yes">Yes</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <KV label="FIR Date" value={lc.fir.date} mono />
          <KV label="Approved By" value={lc.fir.approved_by_name} />
          <div className="md:col-span-4">
            <Field label="Remarks">
              <Textarea disabled={!perm.can_edit_fir} rows={2}
                        data-testid="fir-remarks"
                        value={lc.fir.remarks || ""}
                        onChange={(e) => setD({ ...d, lifecycle: { ...lc, fir: { ...lc.fir, remarks: e.target.value } } })}
                        onBlur={(e) => e.target.value !== (lc.fir.remarks || "") && patch("fir", { done: lc.fir.done, date: lc.fir.date, remarks: e.target.value })} />
            </Field>
          </div>
        </div>
      </SectionCard>

      {/* Dispatch */}
      <SectionCard title="Dispatch" icon={Truck} testid="lc-dispatch"
                   description="Requires Testing Completed AND FIR Done"
                   badge={<StatusChip status={lc.dispatch.status} testid="lc-dispatch-chip" />}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Dispatch Status">
            <Select disabled={!perm.can_edit_dispatch}
                    value={lc.dispatch.status}
                    onValueChange={(v) => patch("dispatch", { ...lc.dispatch, status: v }, `Dispatch → ${v}`)}>
              <SelectTrigger data-testid="dispatch-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Pending", "Ready", "Dispatched"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Transporter">
            <Input disabled={!perm.can_edit_dispatch}
                   data-testid="dispatch-transporter"
                   defaultValue={lc.dispatch.transporter || ""}
                   onBlur={(e) => e.target.value !== (lc.dispatch.transporter || "") && patch("dispatch", { ...lc.dispatch, transporter: e.target.value || null })} />
          </Field>
          <Field label="Dispatch Date">
            <Input type="date" disabled={!perm.can_edit_dispatch}
                   data-testid="dispatch-date"
                   value={lc.dispatch.dispatch_date || ""}
                   onChange={(e) => patch("dispatch", { ...lc.dispatch, dispatch_date: e.target.value })}
                   className="font-mono" />
          </Field>
          <Field label="Invoice Number">
            <Input disabled={!perm.can_edit_dispatch}
                   data-testid="dispatch-invoice-number"
                   defaultValue={lc.dispatch.invoice_number || ""}
                   onBlur={(e) => e.target.value !== (lc.dispatch.invoice_number || "") && patch("dispatch", { ...lc.dispatch, invoice_number: e.target.value || null })}
                   className="font-mono" />
          </Field>
          <Field label="Invoice Value (₹)">
            <Input type="number" disabled={!perm.can_edit_dispatch}
                   data-testid="dispatch-invoice-value"
                   defaultValue={lc.dispatch.invoice_value ?? ""}
                   onBlur={(e) => {
                     const v = e.target.value === "" ? null : parseFloat(e.target.value);
                     if (v !== lc.dispatch.invoice_value) patch("dispatch", { ...lc.dispatch, invoice_value: v });
                   }}
                   className="font-mono" />
          </Field>
          <Field label="Vehicle No.">
            <Input disabled={!perm.can_edit_dispatch}
                   data-testid="dispatch-vehicle"
                   defaultValue={lc.dispatch.vehicle_number || ""}
                   onBlur={(e) => e.target.value !== (lc.dispatch.vehicle_number || "") && patch("dispatch", { ...lc.dispatch, vehicle_number: e.target.value || null })}
                   className="font-mono" />
          </Field>
          <Field label="LR Number">
            <Input disabled={!perm.can_edit_dispatch}
                   data-testid="dispatch-lr"
                   defaultValue={lc.dispatch.lr_number || ""}
                   onBlur={(e) => e.target.value !== (lc.dispatch.lr_number || "") && patch("dispatch", { ...lc.dispatch, lr_number: e.target.value || null })}
                   className="font-mono" />
          </Field>
        </div>
      </SectionCard>

      {/* Audit trail */}
      <SectionCard title="Audit Trail" icon={CheckCircle2} testid="lc-audit"
                   description="Every stage transition captured (append-only)">
        <AuditTrail items={lc.audit_trail || []} />
      </SectionCard>

      {/* Waive Dialog */}
      <Dialog open={waiveDialog} onOpenChange={setWaiveDialog}>
        <DialogContent data-testid="waive-dialog">
          <DialogHeader>
            <DialogTitle>Waive a Reconditioning Stage</DialogTitle>
            <DialogDescription>Only Design HOD / Admin. Waived stages are marked as N/A and no longer block downstream work.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Field label="Stage to Waive">
              <Select value={waiveStage} onValueChange={setWaiveStage}>
                <SelectTrigger data-testid="waive-stage"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="design">Design</SelectItem>
                  <SelectItem value="customer_drawing">Customer Drawing Approval</SelectItem>
                  <SelectItem value="bom">BOM Preparation</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Reason *">
              <Textarea data-testid="waive-reason" rows={3} value={waiveReason}
                        onChange={(e) => setWaiveReason(e.target.value)}
                        placeholder="Why is this stage not required for this recon job?" />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWaiveDialog(false)}>Cancel</Button>
            <Button data-testid="waive-confirm" onClick={waive} disabled={saving}
                    className="bg-violet-600 hover:bg-violet-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Waive"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
